import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  TableCell,
  Badge,
  Tooltip,
  Chip,
  alpha,
} from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import WorkIcon from "@mui/icons-material/Work";
import DataTable from "bundles/Internal/components/common/DataTable";
import JobOfferTableFilters from "bundles/Internal/components/job_offer/JobOfferTableFilters";
import JobApplicationsCollapse from "bundles/Internal/components/common/JobApplicationsCollapse";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "job_title", label: "Job Title" },
  { key: "company_name", label: "Company" },
  { key: "end_of_mandate", label: "End of Mandate" },
  { key: "company_industries", label: "Industries" },
  { key: "categories", label: "Categories" },
  { key: "job_applications_count", label: "Applications" },
  { key: "locations", label: "Locations" },
  { key: "created_at", label: "Created At" },
  { key: "credit_id", label: "Credit" },
  { key: "job_statuses", label: "Job Statuses" },
  { key: "offer_status", label: "Offer Status" },
  { key: "offer_language", label: "Language" },
  { key: "view_count", label: "Views" },
];

export const INDUSTRY_COLOR = {
  architecture:   { border: "#1976D2", bg: "rgba(25,118,210,0.10)",   text: "#1976D2" },
  engineering:    { border: "#079344", bg: "rgba(7,147,68,0.10)",     text: "#079344" },
  construction:   { border: "#F57C00", bg: "rgba(245,124,0,0.10)",    text: "#F57C00" },
  administration: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)",   text: "#6A1B9A" },
  industry:       { border: "#757575", bg: "rgba(117,117,117,0.10)",  text: "#757575" },
};

export const OFFER_STATUS_COLOR = {
  draft:  { border: "#757575", bg: "rgba(117,117,117,0.10)",  text: "#757575" },
  online: { border: "#079344", bg: "rgba(7,147,68,0.10)",     text: "#079344" },
  closed: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)",    text: "#D32F2F" },
};

export const LANGUAGE_COLOR = {
  french:  { border: "#1976D2", bg: "rgba(25,118,210,0.10)",  text: "#1976D2" },
  dutch:   { border: "#F57C00", bg: "rgba(245,124,0,0.10)",   text: "#F57C00" },
  english: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)",  text: "#6A1B9A" },
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const formatLocations = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) return "";

  return locations
    .map((loc) => {
      if (loc.city) return loc.city;
      if (loc.region) return loc.region;
      if (loc.district) return loc.district;
      if (loc.country) return loc.country;
      return null;
    })
    .filter(Boolean)
    .join(", ");
};

const DATE_KEYS = new Set(["created_at"]);
const DATE_ONLY_KEYS = new Set(["end_of_mandate"]);

function formatJobOfferCell(jobOffer, key) {
  const value = jobOffer?.[key];

  if (DATE_KEYS.has(key)) return formatDateTime(value);
  if (DATE_ONLY_KEYS.has(key)) return formatDate(value);

  if (key === "company_industries" && Array.isArray(value) && value.length > 0) {
    const remaining = value.slice(2);
    return (
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "nowrap" }}>
        {value.slice(0, 2).map((ind) => (
          <span key={ind}>
            {pill(ind, INDUSTRY_COLOR[ind] || INDUSTRY_COLOR.industry)}
          </span>
        ))}
        {value.length > 2 && (
          <Tooltip
            title={
              <Box sx={{ py: 0.5 }}>
                {remaining.map((ind) => (
                  <Box key={ind} sx={{ py: 0.25 }}>
                    {String(ind).replaceAll("_", " ")}
                  </Box>
                ))}
              </Box>
            }
            arrow
            placement="top"
          >
            <Chip
              label={`+${value.length - 2}`}
              size="small"
              sx={{
                fontSize: 11,
                height: 24,
                borderRadius: "8px",
                backgroundColor: "rgba(0,0,0,0.05)",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                },
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  if (key === "categories" && Array.isArray(value) && value.length > 0) {
    const displayed = value.slice(0, 2).join(", ");
    const remaining = value.slice(2);

    if (value.length > 2) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{displayed}</span>
          <Tooltip
            title={
              <Box sx={{ py: 0.5 }}>
                {remaining.map((cat) => (
                  <Box key={cat} sx={{ py: 0.25 }}>
                    {cat}
                  </Box>
                ))}
              </Box>
            }
            arrow
            placement="top"
          >
            <Chip
              label={`+${value.length - 2}`}
              size="small"
              sx={{
                fontSize: 11,
                height: 20,
                borderRadius: "6px",
                backgroundColor: "rgba(0,0,0,0.05)",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                },
              }}
            />
          </Tooltip>
        </Box>
      );
    }

    return displayed;
  }

  if (key === "job_statuses" && Array.isArray(value) && value.length > 0) {
    return value.slice(0, 2).map(s => s.replaceAll("_", " ")).join(", ") + (value.length > 2 ? ` +${value.length - 2}` : "");
  }

  if (key === "offer_status" && value) {
    return pill(value, OFFER_STATUS_COLOR[value] || OFFER_STATUS_COLOR.draft);
  }

  if (key === "offer_language" && value) {
    return pill(value, LANGUAGE_COLOR[value] || LANGUAGE_COLOR.english);
  }

  if (key === "locations") {
    return formatLocations(value);
  }

  if (key === "credit_id") {
    return value ? `#${value}` : "";
  }

  return formatValue(value);
}

const JobOfferTable = ({
  jobOffers = [],
  onJobOfferSelect,
  onOpenNotes,
  onQueryChange,
  filters,
  onFiltersChange,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onExportCsv,
}) => {
  const navigate = useNavigate();
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = useCallback((e, jobOfferId) => {
    e.stopPropagation();
    setExpandedRows((prev) => ({ ...prev, [jobOfferId]: !prev[jobOfferId] }));
  }, []);

  const renderExpandedRow = useCallback(
    (jobOffer) => {
      if (!expandedRows[jobOffer.id]) return null;
      return (
        <JobApplicationsCollapse
          applications={jobOffer.job_applications || []}
          variant="job_offer"
          showTalentFilter
          emptyLabel="No applications for this offer."
        />
      );
    },
    [expandedRows],
  );

  const renderCell = useCallback((jobOffer, key) => {
    if (key === "company_name") {
      const value = jobOffer?.company_name;
      if (!value) return "";
      return (
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            if (jobOffer.company_id) navigate(`/internal/companies/${jobOffer.company_id}`);
          }}
          sx={{
            color: "#079344",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Box>
      );
    }
    return formatJobOfferCell(jobOffer, key);
  }, [navigate]);

  return (
    <DataTable
      title="Job Offers"
      totalLabel={(count) => `${count.toLocaleString()} total job offers`}
      emptyIcon={WorkIcon}
      emptyTitle="No job offers found"
      emptySubtitle="Try adjusting your search criteria"
      columns={columns}
      data={jobOffers}
      renderCell={renderCell}
      expandable
      isRowExpanded={(jobOffer) => !!expandedRows[jobOffer.id]}
      onToggleExpandRow={(jobOffer, e) => toggleExpand(e, jobOffer.id)}
      extraHeadColumns={["Notes"]}
      renderExtraCells={(jobOffer) => {
        const notesCount = Number(jobOffer?.notes_count || 0);
        return (
          <TableCell key="notes" sx={{ whiteSpace: "nowrap", py: 1.5 }}>
            <Tooltip title="View notes" arrow placement="top">
              <IconButton
                size="small"
                aria-label="Open notes"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenNotes?.(jobOffer);
                }}
                disabled={!onOpenNotes}
                sx={{
                  backgroundColor:
                    notesCount > 0
                      ? alpha("#079344", 0.1)
                      : "transparent",
                  "&:hover": {
                    backgroundColor: alpha("#079344", 0.2),
                  },
                }}
              >
                <Badge
                  badgeContent={notesCount}
                  color="primary"
                  invisible={notesCount === 0}
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "#079344",
                      fontWeight: 600,
                      fontSize: 10,
                    },
                  }}
                >
                  <NoteAddOutlinedIcon
                    fontSize="small"
                    sx={{
                      color:
                        notesCount > 0 ? "#079344" : "text.secondary",
                    }}
                  />
                </Badge>
              </IconButton>
            </Tooltip>
          </TableCell>
        );
      }}
      renderExpandedRow={renderExpandedRow}
      onRowClick={onJobOfferSelect}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by job title, company, industry, category…"
      searchInfo="Job title, company, industry"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <JobOfferTableFilters filters={filters} onFiltersChange={onFiltersChange} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
      selectable={selectable}
      selectedRows={selectedRows}
      onRowSelect={onRowSelect}
      onExportCsv={onExportCsv}
    />
  );
};

export default JobOfferTable;
